import * as React from 'react';
import { makeStyles, Tooltip, ButtonBase, Button } from '@material-ui/core';
import theme from '../../theme';
import { NavigateNext, NavigateBefore } from '@material-ui/icons';


const useStyles = makeStyles(() => ({
  root: {
    display: "flex",
    background: theme.colors.White,
    flexDirection: "row",
    fontFamily: theme.fontFamily.MARKPROLIGHT,
    width: "800px",
    borderRadius: "5px",
    border: "1px solid whitesmoke"
  },
  isToday: {
    color: theme.colors.DodgerBlue
  },
  sidePane: {
    "& div": {
      borderRadius: "8px",
      transition: ".2s ease-in",
      padding: "10px",
      "&:hover": {
        cursor: "pointer",
        backgroundColor: "rgba(133, 221, 243, 0.41)"
      },
      marginTop: "2px"
    },
    width: "220px",
    padding: "12px",
  },
  leftPane: {
    width: "calc(800px - 220px)",
    display: "flex",
    flexDirection: "column",
    borderLeft: "1px solid whitesmoke"
  },
  firstBlock: {
    width: "50%",
    padding: "15px",
    borderRight: "1px solid whitesmoke"
  },
  secondBlock: {
    width: "50%",
    padding: "15px"
  },
  blockGrp: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    height: "80%"
  },
  thirdBlock: {
    borderTop: "1px solid whitesmoke",
    padding: '10px',
    fontWeight: 'bold',
    fontSize: '16px',
  },
  calendarGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '5px',
    textAlign: 'center',
    fontSize: '14px',
  },
  dayHeader: {
    fontWeight: 'bold',
    borderBottom: '1px solid #ccc',
    paddingBottom: '5px',
  },
  dateCell: {
    padding: '5px',
    borderRadius: '4px',
    "&:hover": {
      backgroundColor: "#eee",
      cursor: "pointer"
    }
  },
  selected: {
    backgroundColor: "#3399ff",
    color: "white",
    borderRadius: "4px"
  },
  header: {
    alignContent: "center",
    alignItems: "center",
    display: "flex"
  },
  headerLeft: {
    justifyContent: "flex-start",
  },
  headerRight: {
    justifyContent: "flex-end"
  },

  inRange: {
    backgroundColor: "#cce6ff", // light blue for range
    borderRadius: "4px",
  },
  disabled: {
    color: "whitesmoke",
    backgroundColor: "grey"
  },
  pointer: {
    cursor: "pointer"
  },
  prevNavigator: {
    marginRight: "6px",
    borderRadius: "15px"
  },
  nextNavigator: {
    marginLeft: "6px",
    borderRadius: "15px"
  }
}));


export default function CalendarBlock(
  {
    data,
    nextAction,
    prevAction,
    selectedDate,
    onDateClick,
    selectedEndDate,
    setDateEnd,
    temporaryHover,
    setTemporaryHover
  }
) {
  const classes = useStyles();
  const isBetween = (currentDate, start, end, isCurrentMonth) => {

    const toNumber = (d) => d.year * 10000 + d.month * 100 + d.day;
    const n = toNumber(currentDate);
    return toNumber(start) < n && n < toNumber(end);
  };

  const getRankDate = (dateObj, compareTo) => {
    const dateA = new Date(dateObj.year, dateObj.month, dateObj.day);
    const dateB = new Date(compareTo.year, compareTo.month, compareTo.day);

    const msInDay = 1000 * 60 * 60 * 24;
    return Math.round((dateA - dateB) / msInDay); // positive if dateA is after dateB
  };


  return (
    <div>
      <header className={`${classes.header} ${prevAction && !nextAction ? classes.headerLeft : !prevAction && nextAction ? classes.headerRight : null}`}>
        {prevAction ? <ButtonBase className={classes.prevNavigator}>
          <NavigateBefore
            className={classes.pointer}
            onClick={(e) => { prevAction() }} />
        </ButtonBase> : null}
        {data.monthLabel}{nextAction ? <ButtonBase className={classes.nextNavigator}>
          <NavigateNext className={classes.pointer} onClick={(e) => {
            nextAction()
          }} /> </ButtonBase> : null}</header>

      <div className={classes.calendarGrid}>
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(day => (
          <div key={day} className={classes.dayHeader}>{day}</div>
        ))}
        {data.days.map((dateObj, i) => {
          const now = new Date()
          const { day, month, year, isCurrentMonth } = dateObj;
          const d = day;
          const currentDate = { day, month, year };

          const isSelected = selectedDate &&
            selectedDate.day === d &&
            selectedDate.month === data.month &&
            selectedDate.year === data.year;

          const endIsSelected = selectedEndDate &&
            selectedEndDate.day === d &&
            selectedEndDate.month === data.month &&
            selectedEndDate.year === data.year;

          const isInRange =
            selectedDate?.day &&
            selectedEndDate?.day &&
            d &&
            isBetween(currentDate, selectedDate, selectedEndDate, isCurrentMonth);


          const isInTemporaryRange =
            selectedDate?.day &&
            temporaryHover.overActive &&
            temporaryHover?.day &&
            !selectedEndDate?.day &&
            d &&
            isBetween(currentDate, selectedDate, temporaryHover, isCurrentMonth);

          const isToday =
            !isSelected &&
            now.getDate() === day &&
            now.getMonth() === month &&
            now.getFullYear() === year &&
            !isInRange &&
            !isInTemporaryRange;
          return (
            <Tooltip
              key={i}
              arrow title={isSelected ? "Start Date" : !isSelected && isCurrentMonth && (isInRange) ? (
                `${temporaryHover.overActive && temporaryHover.day === currentDate.day ? getRankDate(currentDate, selectedDate) : ''} Days`
              ) : endIsSelected ? `End Date` : ""}>
              <div
                onMouseOver={(e) => {
                  setTemporaryHover({ overActive: true, day, month, year })
                }}
                onMouseLeave={(e) => {
                  setTemporaryHover({ overActive: false, day: 0, month: 0, year: 0 })
                }}
                disabled={!isCurrentMonth}
                className={
                  `${!isCurrentMonth ? classes.disabled : ''} ${classes.dateCell} ${isSelected || endIsSelected ? classes.selected : (isInRange || isInTemporaryRange || isToday) ? classes.inRange : ''
                  } ${isToday && classes.isToday}`}
                onClick={() => {
                  if (!isCurrentMonth) return;
                  if (!d) return;
                  const clickedDate = { day: d, month: data.month, year: data.year };
                  const objToDate = (obj) => (new Date(obj.year, obj.month - 1, obj.day));
                  if (selectedDate.day && selectedEndDate.day) {
                    onDateClick(clickedDate);
                    setDateEnd({});
                    return;

                  }
                  if (isSelected && selectedDate.day === d && !selectedEndDate.day) {
                    onDateClick({})
                    setDateEnd({})
                    return
                  }
                  if (isSelected && selectedDate.day && selectedEndDate.day) {
                    return
                  }
                  if (!isSelected && !selectedDate.day && !selectedEndDate.day) {
                    onDateClick(clickedDate)
                    return
                  }

                  if (!isSelected && selectedDate.day && selectedDate.day < d && !selectedEndDate.day) {
                    setDateEnd(clickedDate);
                    return

                  }
                  if (!isSelected && selectedDate.day && (objToDate(selectedDate) < objToDate(currentDate)) && !selectedEndDate.day) {
                    setDateEnd(clickedDate);
                    return

                  }

                  if (!selectedDate && !selectedDate.year) {
                    onDateClick(clickedDate);
                  } else if (isSelected && clickedDate.d === d) {
                    onDateClick({})
                  } else if (
                    endIsSelected && selectedEndDate.day === d
                  ) {
                    setDateEnd({})
                  } else if (
                    selectedDate &&
                    !selectedEndDate &&
                    (clickedDate.year > selectedDate.year ||
                      (clickedDate.year === selectedDate.year && clickedDate.month > selectedDate.month) ||
                      (clickedDate.year === selectedDate.year &&
                        clickedDate.month === selectedDate.month &&
                        clickedDate.day > selectedDate.day))
                  ) {
                    setDateEnd(clickedDate);
                  } else {
                    onDateClick(clickedDate);
                  }
                }}
              >
                {d || ''}
              </div>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
}